import { Fragment } from "react";
import { Check, Minus } from "lucide-react";
import type { TFunction } from "i18next";

import { Container } from "./ui";
import { useLocale } from "../i18n/useLocale";

/**
 * PricingComparison — features × 5 plans, grouped by category, with green
 * checkmarks. Rendered under the plan cards on the marketing Pricing page.
 *
 * DIT-396: the client's "comparison table with the green checkmarks showing
 * what is included in each plan" was on the old landing but was not carried
 * over when the marketing site was ported, so the public Pricing page showed
 * only the per-card feature bullets. This restores it.
 *
 * The matrix mirrors the funnel plan-step table (`src/pages/plan/ui/
 * ComparisonTable`) so the two never disagree; row/category labels and word
 * cell values resolve under `pricing:comparison.*`.
 *
 * Cell values match the authoritative Time Zero pricing sheet (DIT-345) and the
 * client's review sign-off (DIT-396): the full sheet row set is listed, and
 * data-storage durations are the client-confirmed 5 years (Time Basic / Pro) and
 * 10 years (Project Basic / Pro), with Time Zero keeping none. Keep in sync with
 * the funnel table (`src/pages/plan/ui/ComparisonTable`).
 */

type PlanId = "timeZero" | "timeBasic" | "timePro" | "projectBasic" | "projectPro";

// Word cell values that need translation. Numeric cells ('5', '10', '50') and
// booleans render as-is (numbers) or as icons.
const WORD_VALS = new Set(["upTo4", "unlimited", "soon", "optional", "years5", "years10"]);

const COLS: { id: PlanId; name: string }[] = [
  { id: "timeZero", name: "Time Zero" },
  { id: "timeBasic", name: "Time Basic" },
  { id: "timePro", name: "Time Pro" },
  { id: "projectBasic", name: "Project Basic" },
  { id: "projectPro", name: "Project Pro" },
];

type Cell = boolean | string;
type Row = { key: string; cells: Record<PlanId, Cell> };
type Category = { key: string; rows: Row[] };

// Order matches COLS: [timeZero, timeBasic, timePro, projectBasic, projectPro]
const row = (key: string, tuple: [Cell, Cell, Cell, Cell, Cell]): Row => ({
  key,
  cells: { timeZero: tuple[0], timeBasic: tuple[1], timePro: tuple[2], projectBasic: tuple[3], projectPro: tuple[4] },
});

const CATEGORIES: Category[] = [
  {
    key: "timeAttendance",
    rows: [
      row("languages12", [true, true, true, true, true]),
      row("mobileDesktop", [true, true, true, true, true]),
      row("gpsCheckin", [true, true, true, true, true]),
      row("teamMembers", ["upTo4", "unlimited", "unlimited", "unlimited", "unlimited"]),
      row("manualTransport", [true, true, true, true, true]),
      row("reportsExcel", [true, true, true, true, true]),
      row("workLocations", ["5", "10", "unlimited", "unlimited", "unlimited"]),
      row("frontendLocations", [false, false, true, false, true]),
      row("holidayAbsenceOvertime", [true, true, true, true, true]),
      row("expenseManagement", [true, true, true, true, true]),
      row("irregularMap", [false, true, true, true, true]),
      row("checkInWork", [false, false, false, true, true]),
      row("employeeDocuments", [false, false, true, false, true]),
      row("openApi", [false, false, true, true, true]),
      row("navigation", [false, false, true, true, true]),
      row("vehicleTracking", [false, "optional", "optional", "optional", "optional"]),
      row("registrationsPerMonth", ["50", "unlimited", "unlimited", "unlimited", "unlimited"]),
      row("dataStorage", [false, "years5", "years5", "years10", "years10"]),
    ],
  },
  {
    key: "projectCost",
    rows: [
      row("projectManagement", [false, false, false, true, true]),
      row("projectDescriptions", [false, false, false, true, true]),
      row("subprojects", [false, false, false, true, true]),
      row("subcontractorManagement", [false, false, false, true, true]),
      row("extraWorkMaterials", [false, false, false, true, true]),
      row("transportProjects", [false, false, false, false, true]),
      row("fullSuite", [false, false, false, false, true]),
    ],
  },
  {
    key: "support",
    rows: [
      row("prioritySupport", [false, false, false, false, true]),
      // Split when planning shipped: it is available on Time Pro and Project Pro,
      // while messaging and notifications are still to come. One row saying "soon"
      // for all three would now be wrong in both directions at once.
      row("schedulingPlanning", [false, false, true, false, true]),
      row("messagingNotifications", [false, false, "soon", false, "soon"]),
    ],
  },
];

function CellView({ value, t }: { value: Cell; t: TFunction }) {
  if (value === true) {
    return (
      <span className="inline-grid h-6 w-6 place-items-center rounded-full bg-ds-tint text-ds-accent">
        <Check size={14} strokeWidth={3} />
      </span>
    );
  }
  if (value === false) {
    // Tailwind preflight sets `svg { display: block }`, so a bare icon
    // left-aligns in a text-center cell — mx-auto re-centers it.
    return <Minus size={16} strokeWidth={2} className="mx-auto text-ink-faint" />;
  }
  // Word tokens are translated; numeric strings ('5', '10', '50') render as-is.
  const label = WORD_VALS.has(value) ? t(`pricing:comparison.val.${value}`) : value;
  return <span className="font-ds text-[13px] font-semibold text-ds-ink">{label}</span>;
}

export default function PricingComparison() {
  const { t } = useLocale();
  return (
    <Container className="py-16 font-ds md:py-20">
      <h2 className="font-display text-ds-26 font-semibold tracking-tight text-ds-ink">{t("pricing:comparison.title")}</h2>
      <p className="mt-1 font-sans text-ds-14 text-ink-soft">{t("pricing:comparison.subtitle")}</p>

      <div className="mt-6 overflow-x-auto rounded-3xl border border-line bg-surface">
        <table className="w-full min-w-[760px] table-fixed border-collapse font-ds">
          <colgroup>
            <col className="w-[34%]" />
            {COLS.map((c) => (
              <col key={c.id} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="p-4 text-left align-bottom" />
              {COLS.map((c) => (
                <th
                  key={c.id}
                  className="p-4 text-center align-bottom font-display text-[15px] font-semibold leading-tight tracking-tight text-ds-ink"
                >
                  {c.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CATEGORIES.map((cat) => (
              <Fragment key={cat.key}>
                <tr>
                  <td
                    colSpan={COLS.length + 1}
                    className="bg-bg-subtle px-4 py-2.5 font-ds text-[12px] font-bold uppercase tracking-[0.08em] text-ink-faint"
                  >
                    {t(`pricing:comparison.cat.${cat.key}`)}
                  </td>
                </tr>
                {cat.rows.map((r) => (
                  <tr key={r.key} className="border-t border-line">
                    <td className="px-4 py-3 font-sans text-ds-14 text-ds-ink">{t(`pricing:comparison.row.${r.key}`)}</td>
                    {COLS.map((c) => (
                      <td key={c.id} className="px-4 py-3 text-center align-middle">
                        <CellView value={r.cells[c.id]} t={t} />
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Container>
  );
}
